import type { ImageEffect, Scene, SceneElement } from './types.js';

/**
 * A photo-to-design AI layer: the shopper's photo, the template's prompt with
 * the shopper's own words (name, year) put into it, the template's reference
 * pictures, and the picture that comes back is what prints.
 *
 * The prompt names its words by the text layer that holds them — `{{el:<id>}}`
 * — so the answer reaches the prompt through the same binding that would draw
 * it: the server's resolve, the storefront and the print worker all read the
 * layer, never a second copy of the answer. A layer whose only job is to carry
 * the name into the prompt is simply left invisible.
 *
 * One spec string decides the cache key (see `aiStyleSpec`), so the picture the
 * shopper approved is the picture the press finds, and a different name is a
 * different picture.
 */

export type AiStyleEffect = Extract<ImageEffect, { kind: 'aiStyle' }>;

/** The canvases every provider offers; written into the request, not cropped afterwards. */
export const AI_STYLE_SIZES = ['square', 'portrait', 'landscape'] as const;
export type AiStyleSize = (typeof AI_STYLE_SIZES)[number];

/** How many reference pictures a layer may send with the photo. */
export const AI_STYLE_MAX_REFERENCES = 3;
/** The most of the shopper's words one placeholder takes. */
export const AI_STYLE_MAX_WORDS = 60;

const FIELD = /\{\{\s*el:([A-Za-z0-9_.:-]{1,120})\s*\}\}/g;

/** The text layers a prompt reads, in order, without repeats. */
export function aiStyleFields(prompt: string): string[] {
  const out: string[] = [];
  for (const match of prompt.matchAll(FIELD)) if (!out.includes(match[1]!)) out.push(match[1]!);
  return out;
}

/**
 * The shopper's words as they may enter a prompt: one line, no braces or
 * quotes that could close the template's own wording, at most 60 characters.
 * Letters of every script stay (Đorđe, Миља).
 */
export function cleanAiStyleWords(value: unknown): string {
  const text = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
  return text
    .normalize('NFC')
    .replace(/[\u0000-\u001f\u007f{}<>"`\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, AI_STYLE_MAX_WORDS);
}

/** The prompt with each `{{el:id}}` replaced by that layer's words (empty when unknown). */
export function fillAiStylePrompt(prompt: string, wordsOf: (elementId: string) => unknown): string {
  return prompt.replace(FIELD, (_all, id: string) => cleanAiStyleWords(wordsOf(id))).trim();
}

/** The AI style on an element, when it has one with a prompt. */
export function aiStyleOf(element: SceneElement | undefined): AiStyleEffect | undefined {
  if (!element || element.type !== 'image') return undefined;
  for (const effect of element.effects ?? []) {
    if (effect.kind === 'aiStyle' && effect.prompt.trim()) return effect;
  }
  return undefined;
}

/**
 * What decides the picture, as one string for the cache key: the filled
 * prompt, and only when set the references and the canvas options. A layer
 * with none of the new fields keys exactly as before, so styles made before
 * references existed are still found.
 */
export function aiStyleSpec(effect: Pick<AiStyleEffect, 'references' | 'transparent' | 'size'>, prompt: string): string {
  const extras: string[] = [];
  if (effect.references?.length) extras.push(`refs=${effect.references.join(',')}`);
  if (effect.transparent) extras.push('transparent');
  if (effect.size) extras.push(`size=${effect.size}`);
  return extras.length ? `${prompt.trim()}\n#${extras.join(';')}` : prompt.trim();
}

/** Every text layer's words across a design's scenes, by element id (first non-empty wins). */
export function sceneWords(scenes: readonly Scene[]): Map<string, string> {
  const words = new Map<string, string>();
  for (const scene of scenes) {
    for (const el of scene.elements) {
      if (el.type !== 'text') continue;
      const value = typeof el.value === 'string' ? el.value : '';
      if (!words.get(el.id)) words.set(el.id, value);
    }
  }
  return words;
}

/**
 * Resolved scenes with every AI style's placeholders filled from the design's
 * own text layers. The same object back when no prompt has a placeholder.
 */
export function fillAiStyles(scenes: Scene[]): Scene[] {
  const needs = scenes.some((s) => s.elements.some((el) => {
    const style = aiStyleOf(el);
    return style ? aiStyleFields(style.prompt).length > 0 : false;
  }));
  if (!needs) return scenes;
  const words = sceneWords(scenes);
  return scenes.map((scene) => ({
    ...scene,
    elements: scene.elements.map((el) => {
      const style = aiStyleOf(el);
      if (!style || el.type !== 'image' || !aiStyleFields(style.prompt).length) return el;
      const prompt = fillAiStylePrompt(style.prompt, (id) => words.get(id));
      return { ...el, effects: (el.effects ?? []).map((e) => (e === style ? { ...style, prompt } : e)) };
    }),
  }));
}
